import { TFunction } from 'i18next';
import { createColumnHelper } from '@tanstack/react-table';
import {
  Badge,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { Icons } from '@texas/components/shared/Icons';
import { ArchivedTooltip } from '@texas/components/shared/tooltip/ArchivedTooltip';
import { Material } from '@texas/api/endpoints/metadata/materialsApi';
import { hasValidMaterialPercentages } from './mixedMaterial';

export const materialTableColumns = (
  t: TFunction,
  isMixed: boolean,
  onArchiveOpen: (material: Material) => void,
  onRestore: (id: number) => Promise<void>,
  onEditOpen: (id: number) => void,
  onComplianceOpen: (material: Material) => void,
) => {
  const columnHelper = createColumnHelper<Material>();
  const columns = [
    columnHelper.accessor('code', {
      cell: (info) => info.getValue(),
      header: t('general.code'),
      maxSize: 100,
    }),
    columnHelper.accessor('name', {
      cell: (info) => info.getValue(),
      header: t('general.name'),
      maxSize: 200,
    }),
    columnHelper.display({
      id: 'complianceDocuments',
      header: t('complianceDocumentDefinitions.title'),
      enableSorting: false,
      size: 210,
      cell: (info) =>
        info.row.original.complianceDocuments.length > 0 ? (
          <Flex gap={1} flexWrap="wrap">
            {info.row.original.complianceDocuments.map((document) => (
              <Badge key={document.id} variant="configuration-reference">
                {document.name}
              </Badge>
            ))}
          </Flex>
        ) : null,
    }),
    columnHelper.accessor('description', {
      cell: (info) => info.getValue(),
      header: t('general.description'),
      maxSize: 200,
    }),
    columnHelper.accessor('articleTextField', {
      cell: (info) => info.getValue(),
      header: t('configuration.articleTextField'),
      enableSorting: false,
    }),
    columnHelper.accessor('certificationTextField', {
      cell: (info) => info.getValue(),
      header: t('configuration.certificationTextField'),
      enableSorting: false,
    }),
    columnHelper.accessor('lockArticleAfterSync', {
      cell: (info) => (info.getValue() ? <Icons.Checkmark /> : ''),
      header: t('configuration.lockMtrlAfterSyncCol'),
      enableSorting: true,
    }),
    columnHelper.accessor('displayOrder', {
      cell: (info) => info.getValue(),
      header: t('branch.displayOrder'),
      enableSorting: true,
    }),
    ...(isMixed
      ? [
          columnHelper.accessor('combinationOf', {
            cell: (info) => (
              <VStack align="start" spacing={1}>
                <Flex gap={2} flexWrap="wrap">
                  {info.getValue().map((v) => (
                    <Badge
                      key={v.id}
                      variant="configuration-reference"
                      maxW="100%"
                      whiteSpace="normal"
                    >
                      {v.name} · {v.percentage}%
                    </Badge>
                  ))}
                </Flex>
                {!hasValidMaterialPercentages(info.getValue()) && (
                  <Tooltip label={t('configuration.mixedPercentagesRequired')}>
                    <Text fontSize="xs" color="status.danger">
                      {t('configuration.mixedPercentagesIncomplete')}
                    </Text>
                  </Tooltip>
                )}
              </VStack>
            ),
            header: t('configuration.combinationOf'),
            size: 260,
            enableSorting: false,
          }),
        ]
      : []),
    columnHelper.accessor('id', {
      cell: (info) => (
        <>
          <Flex justifyContent="end" alignItems="center" gap={4}>
            {info.row.original.archived && (
              <ArchivedTooltip archived={info.row.original.archived} />
            )}
            <Menu placement="left" variant="entityDirectory">
              <MenuButton float="right" aria-label={t('general.more')}>
                <Icons.DotsHorizontal boxSize={6} />
              </MenuButton>
              <MenuList key={info.row.original.id}>
                <MenuItem
                  icon={<Icons.FileOutline boxSize={5} />}
                  onClick={() => onComplianceOpen(info.row.original)}
                >
                  {t('complianceDocumentDefinitions.title')}
                </MenuItem>
                <MenuItem
                  icon={<Icons.Pencil boxSize={5} />}
                  onClick={() => onEditOpen(info.row.original.id)}
                >
                  {t('general.edit')}
                </MenuItem>
                {info.row.original.archived ? (
                  <MenuItem
                    color="entityDirectory.primaryBackground"
                    icon={<Icons.Undo boxSize={5} />}
                    onClick={() => onRestore(info.row.original.id)}
                  >
                    {t('general.restore')}
                  </MenuItem>
                ) : (
                  <MenuItem
                    color="entityDirectory.dangerText"
                    icon={<Icons.Archive boxSize={5} />}
                    onClick={() => onArchiveOpen(info.row.original)}
                  >
                    {t('general.archive')}
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          </Flex>
        </>
      ),
      header: '',
      enableSorting: false,
      maxSize: 100,
    }),
  ];

  return columns;
};
